import React, { useCallback, useMemo, useState } from "react";
import { useStoreDispatch } from "../../../../store/hooks";
import { useSelector } from "react-redux";
import { ITask } from "../../../../store/api/tasks/types";
import { basicDialogSelector } from "../../../../store/helpers/helperSelectors";
import { useLocation } from "react-router-dom";
import { useActivityPerformanceDisplay } from "./hooks";
import CustomDataGrid from "../../../../components/Shared/Table/CustomDataGrid";
import { activityNRPerformanceColumns, activityPerformanceColumns } from "../../../../components/Shared/Table/Columns";
import SearchBox from "../../../../components/Shared/SearchBox/SearchBox";
import { setBasicDialogOpenFor } from "../../../../store/helpers/helperReducers";
import { DIALOGOPENFOR, PerformanceLteData, PerformanceNRData } from "../../../../globals/globals";
import CellKpiChart from "../../../../pages/DeviceDetail/EmbeddedCell/CellKpisChart";
import DeviceDrawer from "../../../../components/Drawers/DeviceDrawer";
import { selectedDeviceSelector } from "../../../../store/api/devices/selectors";
import { IFullDeviceDetails } from "../../../../store/api/deviceConfig/types";
import { startDateSelector } from "../../../../store/api/admin/selectors";
import { setStartDate } from "../../../../store/api/admin/reducers";
import { resetKeyword } from "../../../../store/search/reducer";
import { searchKeywordSelector } from "../../../../store/search/selector";
import ActiveFilterChips from "../../../../components/Shared/Filter/ActiveFilterChips";
import { setRegionFilterIds } from "../../../../store/filters/Nodes/reducer";
import { Button } from "../../../../components/ui/button";
import { Icon } from "../../../../components/Shared/Icon";
import { RadixSelectControl } from "../../../../components/ui/radix-select";
import { RadixFilterMultiSelect } from "../../../../components/ui/radix-multi-select";
import { Tabs, TabsList, TabsTrigger } from "../../../../components/ui/tabs";
import { FilterX } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent } from "../../../../components/ui/tooltip";

const Activity = () => {
  const location = useLocation();
  const { rowData, selectedTab } = location.state || {};

  const {
    selectedRow,
    setSelectedRow,
    rowClicked,
    handleRowClicked,
    data,
    isLoading,
    healthFilter,
    setHealthFilter,
    setIsLoading,
    networkValue,
    setNetworkValue,
    setRowClicked,
    handleResetFilter,
    regionFilterIds,
    regionOptions,
    handleRegionChange,
    search
  } = useActivityPerformanceDisplay(selectedTab);
  const dispatch = useStoreDispatch();
  const startDate = useSelector(startDateSelector);
  const searchKeyword = useSelector(searchKeywordSelector);

  const [selectedRowData, setSelectedRowData] = useState<ITask>();
  const selectedDevice: IFullDeviceDetails = useSelector(selectedDeviceSelector);

  const hasActiveFilters = (
    (regionFilterIds && regionFilterIds.length > 0) ||
    healthFilter !== "all" ||
    (search?.length > 0) ||
    startDate !== 24 * 60 * 60 * 1000 ||
    networkValue !== "LTE"
  );


  const handleNetworkChange = (value: string) => {
    setNetworkValue(value);
    dispatch(resetKeyword());
  };
  const basicDialogOpenFor = useSelector(basicDialogSelector);

  const timeOptions = [
    { value: String(7 * 24 * 60 * 60 * 1000), label: "Last 7 days" },
    { value: String(30 * 60 * 1000), label: "Last 30 minutes" },
    { value: String(60 * 60 * 1000), label: "Last hour" },
    { value: String(24 * 60 * 60 * 1000), label: "Last 24 hours" },
  ];

  const networkOptions = [
    { value: "LTE", label: "LTE" },
    { value: "NR", label: "NR" },
  ];

  const healthFilters = [
    { value: "all", label: "ALL" },
    { value: "good", label: "Optimal" },
    { value: "bad", label: "Degraded" },
  ];

  const handleSelect = (value: string) => {
    if (value) {
      dispatch(setStartDate(Number(value)));
    }
  };

  const handleCloseButtonClicked = (value: boolean) => {
    setRowClicked(value);
  };

  const regionSelectOptions = React.useMemo(() => {
    return (regionOptions || []).map((opt: any) => ({ value: opt.objectKey, label: opt.name || "" }));
  }, [regionOptions]);

  const defaultStartDate = 24 * 60 * 60 * 1000;

  const performanceFilters = useMemo(() => [
    {
      id: "timeRange",
      label: "Time",
      value: String(startDate),
      setValue: ((_: any) => setStartDate(defaultStartDate)) as any,
      values: timeOptions.map((o) => ({ text: o.label, value: o.value })),
    },
    {
      id: "healthFilter",
      label: "Status",
      value: healthFilter,
      setValue: ((_: any) => { setHealthFilter("all"); return { type: "NOOP" }; }) as any,
      values: healthFilters.filter((f) => f.value !== "all").map((f) => ({ text: f.label, value: f.value })),
    },
    {
      id: "regionFilter",
      label: "Region",
      value: regionFilterIds && regionFilterIds.length > 0 ? regionFilterIds : [],
      setValue: setRegionFilterIds as any,
      values: regionSelectOptions.map((o: any) => ({ text: o.label, value: o.value })),
      multiple: true,
    },
    {
      id: "networkFilter",
      label: "Network",
      value: networkValue,
      setValue: ((_: any) => { setNetworkValue("LTE"); return { type: "NOOP" }; }) as any,
      values: networkOptions.map((o) => ({ text: o.label, value: o.value })),
    },
  ], [startDate, healthFilter, regionFilterIds, regionSelectOptions, networkValue]);

  const handleClearSearch = useCallback(() => {
    dispatch(resetKeyword());
  }, [dispatch]);

  const handleClearAllFilters = useCallback(() => {
    handleResetFilter();
    setNetworkValue("LTE");
  }, [handleResetFilter, setNetworkValue]);

  return (
    <>
      {basicDialogOpenFor === DIALOGOPENFOR.CELLKPILTECHART && <CellKpiChart
        kpiData={networkValue === "LTE" ? PerformanceLteData : PerformanceNRData}
      />}
      {rowClicked && <DeviceDrawer data={selectedDevice} closedButtonClicked={handleCloseButtonClicked} />}
      <div
        className="flex flex-col w-full"
        style={{ color: "var(--foreground)" }}
      >
        <div
          className="bg-card text-card-foreground shadow">
          <div className="flex flex-col">
            <div className="pt-4 px-1">
              {/* Filters row */}
              <div className="flex items-center flex-wrap gap-3 mb-3">
                <SearchBox tooltip={"Search by Device"} />
                {/* Region multi-select */}
                <RadixFilterMultiSelect
                  options={regionSelectOptions}
                  value={regionFilterIds || []}
                  onValueChange={(selectedIds) => {
                    handleRegionChange({ target: { value: selectedIds } } as any);
                  }}
                  placeholder="Region"
                  width="200px"
                />
                {/* Network selector */}
                <div style={{ width: "100px" }}>
                  <RadixSelectControl
                    value={networkValue}
                    onValueChange={handleNetworkChange}
                    options={networkOptions}
                    placeholder="__placeholder__"
                  />
                </div>
                {/* Time selector */}
                <div style={{ width: "150px" }}>
                  <RadixSelectControl
                    value={String(startDate)}
                    onValueChange={handleSelect}
                    options={timeOptions}
                    placeholder="__placeholder__"
                  />
                </div>
                {/* Health toggle buttons */}
                <div className="inline-flex rounded-md border border-border overflow-hidden h-8">
                  {healthFilters.map((filter) => (
                    <button
                      key={filter.value}
                      type="button"
                      disabled={isLoading}
                      onClick={() => setHealthFilter(filter.value)}
                      className={`px-3 text-sm font-medium transition-colors ${healthFilter === filter.value
                        ? "bg-primary text-primary-foreground"
                        : "bg-transparent text-muted-foreground hover:bg-accent"
                        } ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                    >
                      {filter.label}
                    </button>
                  ))}
                </div>
                {/* Legend button */}
                <Button
                  variant="outline"
                  size="icon"
                  className="h-4 w-4 shrink-0"
                  onClick={() => dispatch(setBasicDialogOpenFor(DIALOGOPENFOR.CELLKPILTECHART))}
                  aria-label="Legend"
                >
                  <Icon name="info_outline" size={18} />
                </Button>
                {/* Reset filter */}
                {hasActiveFilters && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-6 w-8 shrink-0"
                        onClick={() => { handleResetFilter(); setNetworkValue("LTE"); }}
                        aria-label="Reset All"
                      >
                        <FilterX className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Reset All</TooltipContent>
                  </Tooltip>
                )}
              </div>
              {/* Results count + Active filter chips */}
              <div className="flex justify-between items-end mb-0 mt-4">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-muted-foreground">{`Showing ${data?.length || 0} results`}</span>
                  <ActiveFilterChips
                    filters={performanceFilters}
                    searchKeyword={searchKeyword}
                    onClearSearch={handleClearSearch}
                    onClearAll={handleClearAllFilters}
                    dispatch={dispatch}
                  />
                </div>
              </div>
              <CustomDataGrid
                tableId="ltePerformance"
                sx={{
                  "& .MuiDataGrid-cell": {
                    color: "var(--muted-foreground)",
                    border: "0px solid var(--border)",
                  },
                }}
                rows={isLoading ? [] : data}
                columns={networkValue === "LTE" ? activityPerformanceColumns : activityNRPerformanceColumns}
                rowCount={data?.length}
                loading={isLoading}
                disableColumnMenu={true}
                getRowId={(row: any) => row.siteName}
                onRowClick={handleRowClicked}
                sortingMode="client"
                paginationMode="client"
                fillContainer
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Activity;
